import axios from 'axios';

const TOKEN_STORAGE_KEY = 'duckdrop-access-token';
const TOKEN_EXPIRES_AT_KEY = 'duckdrop-token-expires-at';
export const SESSION_EXPIRED_FLAG_KEY = 'duckdrop-session-expired-notice';
export const SESSION_EXPIRED_MESSAGE = 'Session expired. Please login again.';
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

let onUnauthorized = null;

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
});

function isCredentialAuthRequest(url = '') {
  return (
    url.includes('/api/auth/login') ||
    url.includes('/api/auth/register') ||
    url.includes('/api/auth/change-password')
  );
}

/** @returns {'valid' | 'expired' | 'none'} */
export function getSessionStatus() {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);

  if (!token) {
    return 'none';
  }

  if (!expiresAt || Number.isNaN(Number(expiresAt)) || Date.now() >= Number(expiresAt)) {
    return 'expired';
  }

  return 'valid';
}

api.interceptors.request.use((config) => {
  // Client TTL expired while using the app — logout on next request (no polling).
  if (getSessionStatus() === 'expired') {
    clearAccessToken();

    if (!isCredentialAuthRequest(config.url)) {
      onUnauthorized?.();
      return Promise.reject(new Error(SESSION_EXPIRED_MESSAGE));
    }
  }

  const token = getAccessToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Server rejected the JWT (or missing auth) — treat as session end.
    if (error.response?.status === 401 && !isCredentialAuthRequest(error.config?.url)) {
      clearAccessToken();
      onUnauthorized?.();
    }

    const message =
      error.response?.data?.error ||
      error.message ||
      'Something went wrong. Please try again.';
    return Promise.reject(new Error(message));
  }
);

export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler;
}

export function setAccessToken(token, expiresInMs = SESSION_TTL_MS) {
  if (token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(TOKEN_EXPIRES_AT_KEY, String(Date.now() + expiresInMs));
    return;
  }

  clearAccessToken();
}

export function clearAccessToken() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
}

export function isTokenExpired() {
  return getSessionStatus() !== 'valid';
}

export function getAccessToken() {
  if (getSessionStatus() !== 'valid') {
    clearAccessToken();
    return null;
  }

  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function hasValidSession() {
  return getSessionStatus() === 'valid';
}

export async function apiGet(path) {
  const { data } = await api.get(path);
  if (!data?.success) {
    throw new Error(data?.error || 'Request failed');
  }
  return data.data;
}

export async function apiPost(path, body) {
  const { data } = await api.post(path, body);
  if (!data?.success) {
    throw new Error(data?.error || 'Request failed');
  }
  return data;
}

export async function apiPut(path, body) {
  const { data } = await api.put(path, body);
  if (!data?.success) {
    throw new Error(data?.error || 'Request failed');
  }
  return data.data;
}

export async function apiDelete(path, body) {
  const { data } = await api.delete(path, body ? { data: body } : undefined);
  if (!data?.success) {
    throw new Error(data?.error || 'Request failed');
  }
  return data.data;
}

export async function apiPostForm(path, formData) {
  const { data } = await api.post(path, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (!data?.success) {
    throw new Error(data?.error || 'Request failed');
  }
  return data.data;
}

export default api;
